/**
 * Custom hook for WebSocket simulation events
 * Provides easy-to-use interface for simulation status updates
 */

import { useEffect, useRef, useState } from 'react';
import socketService from '../services/SocketService';

interface SimulationEvent {
  simulation_id: number;
  name: string;
  status: 'started' | 'completed' | 'failed';
  error?: string;
  timestamp: string;
}

interface UseSimulationSocketOptions {
  onSimulationStarted?: (data: SimulationEvent) => void;
  onSimulationCompleted?: (data: SimulationEvent) => void;
  onSimulationFailed?: (data: SimulationEvent) => void;
  autoConnect?: boolean;
}

interface UseSimulationSocketReturn {
  isConnected: boolean;
  connectionStatus: 'connected' | 'connecting' | 'disconnected';
  connect: () => Promise<void>;
  disconnect: () => void;
  subscribeToSimulation: (simulationId: number) => void;
}

export const useSimulationSocket = (
  options: UseSimulationSocketOptions = {},
): UseSimulationSocketReturn => {
  const {
    onSimulationStarted,
    onSimulationCompleted,
    onSimulationFailed,
    autoConnect = true,
  } = options;

  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<
    'connected' | 'connecting' | 'disconnected'
  >('disconnected');
  const callbacksRef = useRef({ onSimulationStarted, onSimulationCompleted, onSimulationFailed });
  const subscriberIdRef = useRef<string | null>(null);

  // Update callbacks ref when they change
  useEffect(() => {
    callbacksRef.current = { onSimulationStarted, onSimulationCompleted, onSimulationFailed };
  }, [onSimulationStarted, onSimulationCompleted, onSimulationFailed]);

  // Auto-connect on mount with defensive check
  useEffect(() => {
    if (!autoConnect) {
      return () => {
        // Unsubscribe this specific subscriber on cleanup
        if (subscriberIdRef.current) {
          socketService.unsubscribeSubscriber(subscriberIdRef.current);
          subscriberIdRef.current = null;
        }
      };
    }

    // Defer connection to ensure all contexts are ready
    const timer = setTimeout(() => {
      try {
        connect().catch((error) => {
          console.error('WebSocket connection error:', error);
          setConnectionStatus('disconnected');
        });
      } catch (error) {
        console.error('WebSocket setup error:', error);
        setConnectionStatus('disconnected');
      }
    }, 250); // Increased delay to ensure Router context is ready

    return () => {
      clearTimeout(timer);
      // Cleanup: unsubscribe this specific subscriber
      if (subscriberIdRef.current) {
        socketService.unsubscribeSubscriber(subscriberIdRef.current);
        subscriberIdRef.current = null;
      }
    };
  }, [autoConnect]);

  // Connect function
  const connect = async (): Promise<void> => {
    try {
      setConnectionStatus('connecting');

      await socketService.connect();

      // Subscribe to events with current callbacks
      const subscriberId = socketService.subscribeToEvents({
        onSimulationStarted: (data: SimulationEvent) => {
          callbacksRef.current.onSimulationStarted?.(data);
        },
        onSimulationCompleted: (data: SimulationEvent) => {
          callbacksRef.current.onSimulationCompleted?.(data);
        },
        onSimulationFailed: (data: SimulationEvent) => {
          callbacksRef.current.onSimulationFailed?.(data);
        },
        onConnectionStatus: (connected: boolean) => {
          setIsConnected(connected);
          setConnectionStatus(connected ? 'connected' : 'disconnected');
        },
      });
      subscriberIdRef.current = subscriberId;

      setIsConnected(true);
      setConnectionStatus('connected');
    } catch (error) {
      console.error('Failed to connect to WebSocket:', error);
      setIsConnected(false);
      setConnectionStatus('disconnected');
      throw error;
    }
  };

  // Disconnect function — only unsubscribes this hook; never kills the global socket
  const disconnect = (): void => {
    if (subscriberIdRef.current) {
      socketService.unsubscribeSubscriber(subscriberIdRef.current);
      subscriberIdRef.current = null;
    }
    setIsConnected(false);
    setConnectionStatus('disconnected');
  };

  // Subscribe to specific simulation
  const subscribeToSimulation = (simulationId: number): void => {
    socketService.subscribeToSimulation(simulationId);
  };

  return {
    isConnected,
    connectionStatus,
    connect,
    disconnect,
    subscribeToSimulation,
  };
};
